import { defineComponent as _defineComponent } from 'vue'
import { ref as _ref, computed as _computed } from 'vue';

import { useSlots } from 'vue';

export type SiffletButtonVariant = 'elevated' | 'outlined' | 'text';
export type SiffletButtonColor = 'primary' | 'secondary' | 'danger' | 'ai' | 'jira' | 'slack' | 'selected' | 'servicenow' | 'siffletBrandPrimary';
export type SiffletButtonSize = 'x-small' | 'small' | 'default' | 'large';
export type SiffletButtonIconAlignment = 'left' | 'right';

export interface SButtonProps {
  /**
   * Async Action to be executed with loading state. If provided, the button will be in loading state until being executed. Not linked to @click in your instanciated component.
   *
   */
  asyncAction?: () => Promise<any>
  /**
   * Route to navigate to.
   */
  to?: string | object | null
  /**
   * Whether the button is disabled or not.
   */
  disabled?: boolean
  /**
   * Text to be displayed in the button.
   */
  text?: string
  /**
   * Tooltip to be displayed when hovering the button.
   */
  tooltip?: string | null
  /**
   * Icon to be displayed in the button.
   */
  icon?: string | null
  /**
   * Button size (`x-small`, `small`, `default` or `large`). `default` by default.
   */
  size?: SiffletButtonSize
  /**
   * Button variant (`elevated`, `outlined` or `text`). `elevated` by default.
   */
  variant?: SiffletButtonVariant
  /**
   * Button color (`primary`, `secondary`, `danger` or `ai`). `primary` by default.
   */
  color?: SiffletButtonColor
  /**
   * Icon alignment (`left` or `right`). `left` by default.
   */
  iconAlignment?: SiffletButtonIconAlignment
  /**
   * data-track attribute for testing purposes.
   */
  dataTrack?: string
  /**
   * Whether the button is in beta or not.
   */
  beta?: boolean
}
type Emits = {
  /**
   * Click event emitted when the button is clicked.
   */
  (event: 'click', e: MouseEvent): void
}

import { defineComponent as __MACROS_defineComponent } from "vue";
const __default__ = __MACROS_defineComponent({
  inheritAttrs: false,
});

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'SButton',
  props: {
  asyncAction: { type: Function },
  to: null,
  disabled: { type: Boolean, default: false },
  text: null,
  tooltip: null,
  icon: null,
  size: { default: 'default' },
  variant: { default: 'elevated' },
  color: { default: 'primary' },
  iconAlignment: { default: 'left' },
  dataTrack: null,
  beta: { type: Boolean, default: false }
},
  emits: ["click"],
  setup(__props, { emit: emits }) {

const props = __props;


;

const slots = useSlots();

let loading = _ref(false);

const vuetifyColor = _computed(() => {
  switch (props.color) {
    case 'primary':
      return 'primary';
    case 'secondary':
      return 'secondary';
    case 'danger':
      return 'alert';
    case 'ai':
      return 'iconAi';
    case 'jira':
      return 'jira';
    case 'servicenow':
      return 'servicenow';
    case 'siffletBrandPrimary':
      return 'siffletBrandPrimary';
    default:
      return 'primary';
  }
});

const classes = _computed(() => {
  const result = [];
  result.push(`v-btn--color-${props.color}`);
  if ((props.icon && !props.text) || (slots.icon && !props.text)) {
    result.push('icon-only');
  }
  return result;
});

const isExternalLink = _computed(() => {
  if (!props.to) return false;

  return props.to.toString().startsWith('http');
});

const computedBindings = _computed(() => ({
  to: !isExternalLink.value ? props.to : null,
  href: isExternalLink.value ? props.to : null,
  target: isExternalLink.value ? '_blank' : null,
}));

const click = async (event: MouseEvent) => {
  if (!props.to) {
    event.preventDefault();
  }
  if (props.asyncAction) {
    loading.value = true;
    try {
      await props.asyncAction();
    } finally {
      loading.value = false;
    }
  } else {
    emits('click', event);
  }
};

return { __sfc: true,props, emits, slots, loading, vuetifyColor, classes, isExternalLink, computedBindings, click, __MACROS_defineComponent, _ref, _computed, useSlots }
}

})